import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Copy, Plug, Trash2 } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useOrgOAuthGrants } from '../../hooks/useOrgOAuthGrants';
import { OAuthGrant } from '../../types/oauth';
import { getMcpServerUrl } from '../../utils/api';
import { summarizeApiKeyScopes } from '../../utils/apiKeyScopes';

interface OrgConnectedAppsSectionProps {
  organizationId: string;
}

const formatLastUsed = (iso: string | null): string => {
  if (!iso) return 'Never used';
  try {
    return `Last used ${formatDistanceToNow(new Date(iso), { addSuffix: true })}`;
  } catch {
    return 'Last used recently';
  }
};

const formatConnectedBy = (grant: OAuthGrant): string => {
  const connected = new Date(grant.created_at).toLocaleDateString();
  return `Connected on ${connected} by ${grant.user_email || grant.user_id}`;
};

/**
 * Remote MCP clients connected to this organization via OAuth.
 * Members see and disconnect their own; admins see and can disconnect everyone's.
 */
const OrgConnectedAppsSection: React.FC<OrgConnectedAppsSectionProps> = ({ organizationId }) => {
  const { isAdmin } = useOrganization();
  const { grants, isLoading, error, reload, revokeGrant } = useOrgOAuthGrants(organizationId);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const mcpUrl = getMcpServerUrl();

  const handleRevoke = async (grant: OAuthGrant) => {
    try {
      setRevokingId(grant.id);
      await revokeGrant(grant.id);
      toast.success(`${grant.client_name} disconnected`);
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to disconnect';
      toast.error(typeof message === 'string' ? message : 'Failed to disconnect');
    } finally {
      setRevokingId(null);
      setConfirmingId(null);
    }
  };

  const copyMcpUrl = async () => {
    if (!mcpUrl) return;
    try {
      await navigator.clipboard.writeText(mcpUrl);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => void reload()}
          className="mt-2 text-sm text-brand-primary dark:text-brand-primaryDarkMode hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-ui-text-tertiary" />
        <h3 className="text-lg font-semibold text-ui-text-primary">Connected apps ({grants.length})</h3>
      </div>

      <p className="text-sm text-ui-text-secondary">
        Any AI client or agent that supports the Model Context Protocol (MCP) can connect to this organization
        through the Kolega MCP server. Each connection acts as the person who approved it and is limited by their
        role.{' '}
        {isAdmin ? 'You can see every connection in this organization.' : 'You can see the connections you made.'}
      </p>

      {mcpUrl && (
        <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary p-4 space-y-2">
          <p className="text-sm font-medium text-ui-text-primary">Connect an app</p>
          <p className="text-xs text-ui-text-tertiary">
            Add this URL as a remote MCP server (Streamable HTTP) in your MCP client. It will open your browser to
            sign in and approve access.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate px-3 py-1.5 text-sm font-mono rounded bg-ui-background-tertiary text-ui-text-primary">
              {mcpUrl}
            </code>
            <button
              type="button"
              onClick={() => void copyMcpUrl()}
              className="p-2 rounded text-ui-text-tertiary hover:text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
              title="Copy URL"
              aria-label="Copy MCP server URL"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {grants.length === 0 ? (
        <div className="border border-dashed border-ui-border-primary rounded-lg p-8 text-center">
          <Plug className="w-10 h-10 mx-auto text-ui-text-tertiary mb-3" />
          <p className="text-sm font-medium text-ui-text-primary">No connected apps</p>
          <p className="mt-1 text-sm text-ui-text-tertiary">
            Apps appear here after someone approves a connection.
          </p>
        </div>
      ) : (
        <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
          {grants.map((grant) => {
            const scopeSummary = summarizeApiKeyScopes(grant.scopes);
            const isRevoking = revokingId === grant.id;
            const isConfirming = confirmingId === grant.id;
            return (
              <div
                key={grant.id}
                className="flex items-center justify-between gap-3 p-4 border-b border-ui-border-primary last:border-b-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-brand-primary/10 dark:bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                    <Plug className="w-4 h-4 text-brand-primary dark:text-brand-primaryDarkMode" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-ui-text-primary truncate">{grant.client_name}</p>
                      <span
                        className="inline-flex flex-shrink-0 items-center rounded-full border border-ui-border-primary px-2 py-0.5 text-xs font-medium text-ui-text-secondary"
                        title={scopeSummary.title}
                      >
                        {scopeSummary.label}
                      </span>
                    </div>
                    <p className="text-xs text-ui-text-tertiary truncate">
                      {formatLastUsed(grant.last_used_at)} · {formatConnectedBy(grant)}
                    </p>
                  </div>
                </div>

                {isConfirming ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setConfirmingId(null)}
                      disabled={isRevoking}
                      className="px-2 py-1 text-xs font-medium text-ui-text-secondary hover:text-ui-text-primary disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRevoke(grant)}
                      disabled={isRevoking}
                      className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded flex items-center gap-1 disabled:opacity-50"
                    >
                      {isRevoking && <Spinner size={12} tone="current" />}
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(grant.id)}
                    className="p-2 rounded text-ui-text-tertiary hover:text-red-600 dark:hover:text-red-400
                               hover:bg-ui-background-tertiary transition-colors"
                    title="Disconnect"
                    aria-label={`Disconnect ${grant.client_name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrgConnectedAppsSection;
